import React, { useEffect, useState } from 'react';
import { styled, Tab, Tabs } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import {
    fetchTopInboundLanes,
    fetchTopOutboundLanes,
} from '../../state/features/topLanesSlice';
import { Order } from '../../interfaces/services/orders';
import { ApiStatus } from '../../utils/enums';
import { useFilters } from '../Filters/useFilters';
import TopShippersTable from '../Tables/TopShippersTable';
import TopLanesTable from '../Tables/TopLanesTable';

const classesPrefix = 'performance';

const classes = {
    root: `${classesPrefix}-root`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.root}`]: {
            minHeight: 250,
            flexGrow: 1,
            overflowX: 'auto',
            '@media (max-height: 1162px)': {
                minHeight: 265,
            },
        },
    };
});

enum PerformerTabs {
    TopShippers = 'TopShippers',
    TopInboundLanes = 'TopInboundLanes',
    TopOutboundLanes = 'TopOutboundLanes'
}

const Performance = ({
    orders,
    loadingTopShippers,
    inboundId,
    outboundId,
}: {
    orders: Order[];
    loadingTopShippers: boolean;
    inboundId: string | null;
    outboundId: string| null;
}): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const filters = useFilters();

    const [selectedTab, setSelectedTab] = useState(PerformerTabs.TopShippers);

    const topInboundLanesApiStatus = useAppSelector((state) => { return state.topLanes.topInboundLanesApiStatus; });
    const topInboundLanes = useAppSelector((state) => { return state.topLanes.topInboundLanes; });

    const topOutboundLanesApiStatus = useAppSelector((state) => { return state.topLanes.topOutboundLanesApiStatus; });
    const topOutboundLanes = useAppSelector((state) => { return state.topLanes.topOutboundLanes; });

    useEffect(() => {
        if (inboundId) {
            dispatch(fetchTopInboundLanes({
                delivery_service_area_id: inboundId,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, inboundId, filters]);

    useEffect(() => {
        if (outboundId) {
            dispatch(fetchTopOutboundLanes({
                pickup_service_area_id: outboundId,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, outboundId, filters]);

    const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newTab: PerformerTabs): void => {
        setSelectedTab(newTab);
    };

    return (
        <StyledDiv className={classes.root}>
            <Tabs
                variant='fullWidth'
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor='primary'
            >
                <Tab value={PerformerTabs.TopShippers} label='Top Shippers' />
                <Tab value={PerformerTabs.TopInboundLanes} label='Top Inbound Lanes' />
                <Tab value={PerformerTabs.TopOutboundLanes} label='Top Outbound Lanes ' />
            </Tabs>
            {
                selectedTab === PerformerTabs.TopShippers &&
                <TopShippersTable
                    loadingTopShippers={loadingTopShippers}
                    orders={orders}
                />
            }
            {
                selectedTab === PerformerTabs.TopInboundLanes &&
                <TopLanesTable
                    laneType='inbound'
                    loadingTopLanes={topInboundLanesApiStatus === ApiStatus.Idle || topInboundLanesApiStatus === ApiStatus.Loading}
                    topLanes={topInboundLanes}
                    entityName={inboundId}
                />
            }
            {
                selectedTab === PerformerTabs.TopOutboundLanes &&
                <TopLanesTable
                    laneType='outbound'
                    loadingTopLanes={topOutboundLanesApiStatus === ApiStatus.Idle || topOutboundLanesApiStatus === ApiStatus.Loading}
                    topLanes={topOutboundLanes}
                    entityName={outboundId}
                />
            }
        </StyledDiv>
    );
};

export default Performance;
